import { firestoreDb } from "$lib/firebase";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";

/**
 * Checks assigned ticket numbers (numbers, not strings) against instant win prizes 
 * on the client side and fetches the parent product's name.
 *
 * @param {object} assignedNumbers - 
 *    An object where keys are prize IDs and values are arrays of assigned ticket numbers (numbers, not strings).
 * @returns {Promise<Array>} - 
 *    An array of matched prizes with their details plus productName.
 */
export default async function checkInstantWins(assignedNumbers) {
  const matchedPrizes = [];
  const db = firestoreDb();

  try {
    // Build an array of promises for each product's doc + subcollection fetch
    const fetchPromises = Object.entries(assignedNumbers).map(async ([prizeId, ticketNumbers]) => {
      const ticketSet = new Set(ticketNumbers);

      // 1) References for the product doc and instantWins subcollection
      const productDocRef = doc(db, "products", prizeId);
      const instantWinsRef = collection(db, "products", prizeId, "instantWins");

      // 2) Fetch both in parallel
      const [productSnap, instantWinsSnap] = await Promise.all([
        getDoc(productDocRef),
        getDocs(instantWinsRef)
      ]);

      // 3) Extract product name (or default to an empty string if missing)
      let productName = "";
      if (productSnap.exists()) {
        const productData = productSnap.data();
        productName = productData?.name ?? "";
      }

      // 4) Check for instant-win matches
      instantWinsSnap.forEach((doc) => {
        const instantWin = doc.data();
        if (ticketSet.has(instantWin.number)) {
          matchedPrizes.push({
            prizeId,
            productName,                // <-- Include the product's name
            ticketNumber: instantWin.number,
            prize: instantWin.prize,
            claimed: instantWin.claimed,
            claimedAt: instantWin.claimedAt,
            claimedBy: instantWin.claimedBy,
            assignedAt: instantWin.assignedAt
          });
        }
      });
    });

    // Wait for all fetches to complete
    await Promise.all(fetchPromises);

    return matchedPrizes;
  } catch (error) {
    console.error("Error checking instant wins:", error);
    throw new Error("Failed to check instant wins");
  }
}
